import { Component, Input } from '@angular/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, Subject, combineLatest, filter, map, startWith, switchMap, take, tap, withLatestFrom, } from 'rxjs';
import { IBaseGetByComanyRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CabinetService } from '../services/cabinet.service';
import { ICompany } from 'src/app/company/models/company';
import { ICabinet } from '../models/cabinet';
import { CabinetStore } from 'src/app/stores/component/cabinet-store';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { Store, select } from '@ngrx/store';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';

@UntilDestroy()
@Component({
    selector: 'app-cabinet-view-client',
    templateUrl: './cabinet-view-client.component.html',
    styleUrl: './cabinet-view-client.component.scss',
    providers: [CabinetStore],
    standalone: false
})
export class CabinetViewClientComponent {

  ////////////////
  // variables //
  //////////////

  vm$: Observable<any>;
  companyId: string;
  loading: boolean = false;


  constructor(
    private cabinetService: CabinetService,
    private store: Store<AppStateInterface>,

  ) {
    this.vm$ = combineLatest([ 
      this.gridState$.pipe(),
      this.store.pipe(select(currentEmployeeSelector)).pipe(tap((employee) => {

        if (employee?.company?.id) {
          this.companyId = employee.company.id;
      
          // Update the search request with the new companyId
          this.searchRequest$.next({
            ...this.searchRequest$.value,
            companyId: this.companyId,
          });
        }
      })),
      this.searchRequest$.pipe(
        filter((request) => request.companyId !== null && request.companyId !== undefined), // Ensure companyId is set
        switchMap((request) => {
          this.loading = true;
          return this.cabinetService.getCabinetsByCompany(request);
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(x => {
          var gridData : GridDataResult = {
            data: x.data,
            total: x.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      )
    ]).pipe(
      map(([
        gridState,
        currentEmployee,
        cabinets,
        searchRequest,
        pageChangeSub
      ]) => ({
        gridState,
        currentEmployee,
        cabinets,
        searchRequest,
        pageChangeSub
      }))
    )
  }

  cabinets: IGridResult<ICabinet[]>;

  ngOnInit(): void {
  }

  /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }


  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseGetByComanyRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

}

function checkProperties(obj: Record<string, any>): boolean {
  return Object.values(obj).every(value => value === null);
}